import React, { useState } from 'react';
import { Button, Input, Form, Row, Col, Typography, DatePicker, Tag, Popconfirm, notification, Tooltip } from 'antd';
import { SearchOutlined, DeleteOutlined, RedoOutlined } from '@ant-design/icons';
import { TableCustom } from '../../../components/TableCustom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getListStoreNotificationsRequest, replayStoreNotificationRequest } from '../../../services/cms/storeNotificationServiceRequest';
import { newObjectKeys } from '../../../utils/utility';
import moment from 'moment';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const QuanLyStoreNoti = () => {
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({ page: 1, limit: 20 });
  const queryClient = useQueryClient();

  const { data: dataNotifications, isLoading } = useQuery({
    queryKey: ['dataStoreNotifications', params],
    queryFn: () => getListStoreNotificationsRequest(params),
  });

  const { mutate: mutateReplay, isLoading: isReplaying } = useMutation({
    mutationFn: (id) => replayStoreNotificationRequest({ id }),
    onSuccess: (data) => {
      if (data?.status === 1 || data?.isSuccess) {
        notification.success({ message: 'Gọi lại thành công!' });
        queryClient.invalidateQueries(['dataStoreNotifications']);
      } else {
        notification.error({ message: data?.message || 'Gọi lại thất bại!' });
      }
    },
    onError: (error) => {
      notification.error({ message: error?.data?.message || 'Có lỗi xảy ra!' });
    },
  });

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.transactionId) temp.transactionId = values.transactionId;
    if (values?.premium_order_id) temp.premium_order_id = values.premium_order_id;
    if (values?.user_id) temp.user_id = Number(values.user_id);
    if (values?.createTimeRange && values.createTimeRange.length === 2) {
      temp.createTimeFrom = values.createTimeRange[0].format('YYYY-MM-DD');
      temp.createTimeTo = values.createTimeRange[1].format('YYYY-MM-DD');
    }
    setParams(temp);
  };

  const hasFilter = params && Object.values(
    newObjectKeys(params, ['transactionId', 'premium_order_id', 'user_id', 'createTimeFrom', 'createTimeTo'])
  ).some((value) => value);

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: 'Thời gian nhận', dataIndex: 'create_time', key: 'create_time', width: 160,
      render: (val) => val ? moment(val).format('DD/MM/YYYY HH:mm:ss') : '--',
    },
    { title: 'Loại thông báo', dataIndex: 'notification_type', key: 'notification_type', width: 220, ellipsis: true },
    { title: 'Subtype', dataIndex: 'subtype', key: 'subtype', width: 120, render: (val) => val || '--' },
    { title: 'Giải thích', dataIndex: 'notification_explain', key: 'notification_explain', width: 220 },
    { title: 'Transaction ID', dataIndex: 'transactionId', key: 'transactionId', width: 160, render: (val) => val || '--' },
    { title: 'Original TxID', dataIndex: 'originalTransactionId', key: 'originalTransactionId', width: 160, render: (val) => val || '--' },
    { title: 'Product ID', dataIndex: 'productId', key: 'productId', width: 180, ellipsis: true },
    {
      title: 'Premium Order ID', dataIndex: 'premium_order_id', key: 'premium_order_id', width: 200, ellipsis: true,
      render: (val) => val ? (
        <a href={`/cms/quan-ly-don-hang?title=${val}`} target="_blank" rel="noopener noreferrer">{val}</a>
      ) : '--',
    },
    {
      title: 'User ID', dataIndex: 'resolved_user_id', key: 'resolved_user_id', width: 100,
      render: (val) => val ? (
        <a href={`/cms/premium-order?title=${val}`} target="_blank" rel="noopener noreferrer">{val}</a>
      ) : '--',
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 120,
      render: (val) => {
        if (val === 1) return <Tag color="success">Thành công</Tag>;
        if (val === 2) return <Tag color="error">Thất bại</Tag>;
        return val !== null && val !== undefined ? <Tag color="default">{val}</Tag> : '--';
      }
    },
    { title: 'Note', dataIndex: 'note', key: 'note', width: 200, ellipsis: true, render: (val) => val || '--' },
    {
      title: 'Action', key: 'action', width: 80, fixed: 'right',
      render: (_, record) => (
        <Popconfirm
          title="Gọi lại notification này?"
          description="Hệ thống sẽ gửi lại payload gốc sang server xử lý."
          onConfirm={() => mutateReplay(record.id)}
          okText="Gọi lại"
          cancelText="Hủy"
        >
          <Tooltip title="Gọi lại">
            <Button
              type="link"
              icon={<RedoOutlined />}
              loading={isReplaying}
              size="small"
            />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl font-bold">Tra cứu Store Notification</h1>
      </div>
      <Form name="search_store_noti" form={formSearch} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="transactionId" label={<Text>Mã giao dịch (TxID / Original TxID)</Text>}>
              <Input placeholder="Nhập TxID hoặc Original TxID..." allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="premium_order_id" label={<Text>Premium Order ID (UUID)</Text>}>
              <Input placeholder="Nhập UUID đơn hàng..." allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="user_id" label={<Text>User ID</Text>}>
              <Input placeholder="Nhập ID người dùng..." allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="createTimeRange" label={<Text>Thời gian nhận</Text>}>
              <RangePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder={['Từ ngày', 'Đến ngày']} />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit"><SearchOutlined /> Tìm kiếm</Button>
            {hasFilter && (
              <Button htmlType="button" onClick={() => { formSearch.resetFields(); setParams({ page: 1, limit: params.limit }); }}>
                <DeleteOutlined /> Xóa lọc
              </Button>
            )}
          </Col>
        </Row>
      </Form>
      <TableCustom columns={columns} dataSource={dataNotifications?.data} totalItem={dataNotifications?.total}
        params={params} setParams={setParams} loading={isLoading} scroll={{ x: 1800 }} />
    </div>
  );
};

export default QuanLyStoreNoti;

